import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import { throwError } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { AlertService } from './alert.service';
import { StorageService } from './storage.service';
import { GlobalService } from './global.service';
import { UtilsService } from './utils.service';

// import {takeUntil} from 'rxjs/internal/operators';

@Injectable()
export class BaseService {
  constructor(
    public http: HttpClient,
    public alertService: AlertService,
    public globalService: GlobalService
  ) {}

  public joinMessages(array: any) {
    let string = '';
    array.forEach((message: any) => {
      string = string + message;
    });
    return string;
  }

  public postRequest(
    url: any,
    data = {},
    handler = { success: null, failure: null },
    alertHide?: any
  ) {
    data = UtilsService.deleteEmptyKeys(data);
    return this.postRequestWithoutDataDeletion(url, data, handler, alertHide);
  }

  public postRequestWithoutDataDeletion(
    url: any,
    data = {},
    handler = {
      success: null,
      failure: null
    },
    alertHide?: any
  ) {
    const headers: HttpHeaders = new HttpHeaders()
      .set('device', 'WEB')
      .set('Authorization', 'Bearer ' + this.globalService.getAccessToken());
    return this.http.post(url, data, { headers: headers }).pipe(
      takeUntil(this.globalService.isSessionExist),
      map((res: any) => {
        if (res && res['message'] && !alertHide) {
          this.alertService.showSuccess(res['message']);
        }
        return res['data'];
      }),
      catchError((err) => {
        return this.handleError(err);
      })
    );
  }

  public patchRequest(url: any, data = {}) {
    data = UtilsService.deleteEmptyKeys(data);
    return this.http.patch(url, data).pipe(
      takeUntil(this.globalService.isSessionExist),
      map((res: any) => {
        if (res['message']) {
          this.alertService.showSuccess(res['message']);
        }
        return res['data'];
      }),
      catchError((err) => {
        return this.handleError(err);
      })
    );
  }

  public deleteRequest(url: any, data = {}) {
    const headers: HttpHeaders = new HttpHeaders()
      .set('device', 'WEB')
      .set('Authorization', 'Bearer ' + this.globalService.getAccessToken());
    data = UtilsService.deleteEmptyKeys(data);
    return this.http.delete(url, { params: data, headers: headers }).pipe(
      takeUntil(this.globalService.isSessionExist),
      map((res: any) => {
        if (res['message']) {
          this.alertService.showSuccess(res['message']);
        }
        return res['data'];
      }),
      catchError((err) => {
        return this.handleError(err);
      })
    );
  }

  public putRequest(url: any, data = {}) {
    data = UtilsService.deleteEmptyKeys(data);
    return this.putRequestWithoutDataDeletion(url, data);
  }

  public putRequestWithoutDataDeletion(url: any, data = {}) {
    const headers: HttpHeaders = new HttpHeaders()
      .set('device', 'WEB')
      .set('Authorization', 'Bearer ' + this.globalService.getAccessToken());
    return this.http.put(url, data, { headers: headers }).pipe(
      takeUntil(this.globalService.isSessionExist),
      map((res: any) => {
        if (res['message']) {
          this.alertService.showSuccess(res['message']);
        }
        return res['data'];
      }),
      catchError((err) => {
        return this.handleError(err);
      })
    );
  }

  public getRequest(url: any, params = {}) {
    const headers: HttpHeaders = new HttpHeaders()
      .set('device', 'WEB')
      .set('Authorization', 'Bearer ' + this.globalService.getAccessToken());
    return this.http
      .get(url, {
        params: params,
        headers: headers
      })
      .pipe(
        takeUntil(this.globalService.isSessionExist),
        map((res: any) => {
          return res['data'];
        }),
        catchError((err) => {
          return this.handleError(err);
        })
      );
  }

  public getRequestWithoutErrorMessage(url: any, params = {}) {
    const headers: HttpHeaders = new HttpHeaders()
      .set('device', 'WEB')
      .set('Authorization', 'Bearer ' + this.globalService.getAccessToken());
    return this.http
      .get(url, {
        params: params,
        headers: headers
      })
      .pipe(
        takeUntil(this.globalService.isSessionExist),
        map((res: any) => {
          return res['data'];
        })
      );
  }

  download_PDF(url, params = {}) {
    const httpHeaders = new HttpHeaders({
      Authorization: 'Bearer ' + this.globalService.getAccessToken(),
      'Content-Type': 'application/json',
      'device' : 'WEB'
    });
    return this.http
      .get(url, { params,headers: httpHeaders,responseType: 'blob'})
      .pipe(
        takeUntil(this.globalService.isSessionExist),
        map((res: Blob) => {
          const blob = new Blob([res], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'invoice.pdf'; // You can customize the file name
          link.click();
          window.URL.revokeObjectURL(url);
        }),
        catchError((err) => {
          return this.handleError(err);
        })
      );
  }

  public handleError(error: HttpErrorResponse) {
    if (error instanceof HttpErrorResponse) {
      if (
        error.status === 401 &&
        error.error &&
        error.error.type !== 'INVALID_CREDENTIALS'
      ) {
        StorageService.clearAll();
        this.globalService.setIsSessionExist();
        this.alertService.showErrors('Session Expired!');
      }
    }
    if (error instanceof ErrorEvent) {
      //console.error('An error occurred:', error?.error?.message);
    } else {
      /*console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);*/
    }
    switch (error.error.type) {
      case 'INTERNAL_SERVER_ERROR':
      case 'RESOURCE_NOT_FOUND':
      case 'FORBIDDEN_ERROR':
      case 'INVALID_CREDENTIALS':
      case 'BUSINESS_LOGIC_ERROR':
      case 'BAD_REQUEST':
        this.alertService.showErrors(error.error.error);
        return throwError(error.error.error);
      case 'VALIDATION':
        const errorMessageObject = error.error.message;
        this.alertService.showErrors(
          this.returnArrayOfErrors(errorMessageObject)
        );
        return throwError(this.returnArrayOfErrors(errorMessageObject));
    }
    return throwError(error);
  }

  public returnArrayOfErrors(errorMessageObject: any) {
    const parsedArray: any = [];
    Object.keys(errorMessageObject).forEach((message) => {
      const keyName = message
        .split('.')
        .join(' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/\w\S*/g, function (txt) {
          return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        });
      const description = this.joinMessages(errorMessageObject[message])
        .split('.')
        .join(' ');
      const errorObject = {
        keyName,
        description,
        completeMessage: `${keyName} : ${description}`
      };
      parsedArray.push(errorObject);
    });
    return parsedArray;
  }

  downloadExcel(url: any, params: any, fileName?: string, onSuccess?: () => void, onError?: (error: any) => void) {

    const httpHeaders = new HttpHeaders({
      Authorization: 'Bearer ' + this.globalService.getAccessToken(),
      'Content-Type': 'application/json',
      'device' : 'WEB'
    });
    const resolvedFileName = fileName
      || (params?.from_date && params?.to_date ? `attendance_${params.from_date}_to_${params.to_date}.xlsx` : null)
      || (params?.date ? `attendance_${params.date}.xlsx` : null)
      || 'attendance.xlsx';
    this.http.post(url, params, { headers: httpHeaders, responseType: 'blob' }).subscribe(
      (response: Blob) => {
        const fileURL = window.URL.createObjectURL(response); // Create a blob URL
        const a = document.createElement('a');
        a.href = fileURL;
        a.download = resolvedFileName;
        document.body.appendChild(a); // Append anchor to the DOM
        a.click(); // Trigger the download
        document.body.removeChild(a); // Remove the anchor after download
        window.URL.revokeObjectURL(fileURL); // Revoke the blob URL after usage
        if (onSuccess) {
          onSuccess();
        }
      },
      (error) => {
        console.error('Download error:', error); // Handle error
        if (onError) {
          onError(error);
        }
      }
    );
  }
}
